import type { EngineState, HealthProbe, HealthSnapshot } from '@/lib/types'
import { formatUsd, toNum, exchangeLabel, exchangeColor } from '@/lib/format'
import { ChartIcon, LayersIcon, RadarIcon, LinkIcon, FlameIcon, AlertIcon } from '@/components/common/icons'
import { InfoHint } from '@/components/common/InfoHint'
import { Metric } from '@/components/common/Metric'

interface StatsPanelProps {
  engine: EngineState
}

function probe(health: HealthSnapshot | null, name: string): HealthProbe | null {
  return health?.probes.find((p) => p.name === name) ?? null
}

function fracPct(value: string | undefined): string {
  const n = toNum(value ?? null)
  if (n === null) return '—'
  return `${(n * 100).toFixed(1)}%`
}

export function StatsPanel({ engine }: StatsPanelProps) {
  const econ = probe(engine.health, 'economic')
  const exposure = probe(engine.health, 'exposure')
  const incidents = probe(engine.health, 'coin_incidents')
  const m = econ?.metrics ?? {}

  // Экспозиция: собираем биржи из ключей метрик `{ex}_equity`/`{ex}_used_margin`/`{ex}_directional_if_down_usd`.
  const exMetrics = exposure?.metrics ?? {}
  const exchanges = new Set<string>()
  for (const key of Object.keys(exMetrics)) {
    const ex = key.replace(/_(equity|used_margin|directional_if_down_usd)$/, '')
    if (ex !== key) exchanges.add(ex)
  }
  const exposureRows = [...exchanges].sort()

  const coinIncidents = Object.entries(incidents?.metrics ?? {})
    .map(([coin, count]) => [coin, Number(count)] as const)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])

  return (
    <div className="panel">
      <div className="panel__head">
        <div>
          <div className="panel__title">Статистика</div>
          <div className="panel__subtitle">
            Экономика dry-run и экспозиция по биржам (наблюдаемость §1.13 G; не торговый порог)
          </div>
        </div>
      </div>

      <div className="panel__pad">
        <div className="metrics metrics--5">
          <Metric
            label="Кандидатов"
            value={m.candidates ?? '—'}
            foot="монет в обзоре"
            icon={<LayersIcon size={18} />}
            tone="info"
            hint="Сколько монет-кандидатов (пересечений инструментов) сейчас в обзоре сканера."
          />
          <Metric
            label="Прошло отбора"
            value={m.opportunities ?? '—'}
            foot="проходных возможностей"
            icon={<RadarIcon size={18} />}
            tone="accent"
            hint="Сколько монет прошли дешёвые фильтры поиска возможностей (кандидаты на вход)."
          />
          <Metric
            label="Открыто входов"
            value={m.entries_opened ?? '0'}
            foot={`не налилось ${m.entries_aborted ?? '0'} (${fracPct(m.aborted_fraction)})`}
            icon={<LinkIcon size={18} />}
            tone="pos"
            hint="Сколько входов одобрено за сессию; «не налилось» — одобренные входы, ордера которых не дали позиции (причина — в истории и журнале)."
          />
          <Metric
            label="Отменено входов"
            value={m.entries_rejected ?? '0'}
            foot={`доля ${fracPct(m.cancelled_fraction)}`}
            icon={<ChartIcon size={18} />}
            tone="muted"
            hint="Экономические отказы decide_entry (порог/P6/sizing) + доля отменённых от всех попыток."
          />
          <Metric
            label="Аварийных выходов"
            value={m.emergency_exits ?? '0'}
            foot={`доля ${fracPct(m.emergency_fraction)}`}
            icon={<FlameIcon size={18} />}
            tone="muted"
            hint="Сколько связок закрыто аварийно (FLATTENING/NEUTRALIZING) + доля от открытых. Гейт live: ≤ 5%."
          />
        </div>

        {m.closed ? (
          <div className="stats-note">
            «Ожид. vs реализ.» по {m.closed} закрытым связкам: средн. ошибка модели{' '}
            <strong>{formatUsd(m.pnl_error_mean_usd, { signed: true })}</strong>, медиана{' '}
            <strong>{formatUsd(m.pnl_error_median_usd, { signed: true })}</strong>.
          </div>
        ) : null}

        <div className="stats-grid">
          <div className="stats-block">
            <div className="stats-block__title">Экспозиция по биржам</div>
            {exposureRows.length === 0 ? (
              <div className="stats-empty">Нет данных счёта (нет открытых связок / подключений).</div>
            ) : (
              <table className="mini-tbl">
                <thead>
                  <tr>
                    <th className="col-lead">Биржа</th>
                    <th>Капитал</th>
                    <th>Исп. маржа</th>
                    <th>
                      При аутейдже
                      <InfoHint text="Направленная экспозиция, если биржа станет недоступна: сумма нотионала связок, у которых одна нога на этой бирже (вторая нога останется без хеджа)." />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {exposureRows.map((ex) => (
                    <tr key={ex}>
                      <td className="col-lead">
                        <span className="ex-chip">
                          <span
                            className="ex-chip__dot"
                            style={{ background: exchangeColor(ex) }}
                          />
                          {exchangeLabel(ex)}
                        </span>
                      </td>
                      <td className="tnum">{formatUsd(exMetrics[`${ex}_equity`])}</td>
                      <td className="tnum">{formatUsd(exMetrics[`${ex}_used_margin`])}</td>
                      <td className="tnum">
                        {formatUsd(exMetrics[`${ex}_directional_if_down_usd`])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="stats-block">
            <div className="stats-block__title">
              Инциденты по монетам
              <InfoHint text="Счётчики инцидентов по монетам (отказы входа и т.п.) для ручного решения оператора по чёрному списку — не авто-блэклист." />
            </div>
            {coinIncidents.length === 0 ? (
              <div className="stats-empty">Инцидентов нет.</div>
            ) : (
              <div className="incident-list">
                {coinIncidents.map(([coin, n]) => (
                  <div key={coin} className="incident">
                    <span className="incident__coin">{coin}</span>
                    <span className="incident__count">
                      <AlertIcon size={12} /> {n}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
